/**
 * @fileoverview The header answer: one small read for the header and for every place that shows the weather now
 * (W24 Job D). The server decides all of it: the frost bar's words (the Today brief's alert, the same words the Today card
 * says), the weather now (the reading in the reader's unit, the sky in the reader's language, the sky's icon key and the
 * period's name), the icon the header's pill draws (the snowflake under a frost tonight, else the sky's), the sentence for a
 * garden the weather service cannot place yet (B44), and how long the phone keeps the answer (keepMinutes, the Grow setting
 * forecast.KEEP_MINUTES). The app draws it as sent.
 *
 * Endpoint: GET /header?lang=&unit= → { success, date, lang, unit, alert, now, pillIcon, noForecastMessage, keepMinutes }.
 * In a shared garden it is the owner's garden and place, in the reader's language and unit (X-Grow-Garden, as the brief).
 * A garden with no postcode Grow can use is a 400 ZIP_REQUIRED; the app does not ask then (WeatherContext waits for the
 * garden place).
 *
 * Who uses it: shared/context/WeatherContext only, which keeps it under [HEADER_QUERY_KEY, garden, language, unit].
 *
 * @module api/header
 */

import { apiClient } from './apiClient';

/**
 * A sky's icon key, the server's (services/weather/skyWords): one of a closed list of ten, each a Lucide icon by name
 * (shared/components/SkyIcon draws it).
 */
export type SkyKey =
  | 'sun' | 'moon' | 'cloud-sun' | 'cloud' | 'cloud-rain' | 'cloud-drizzle' | 'cloud-lightning' | 'cloud-fog' | 'snowflake' | 'wind';

/** The frost bar's alert, as the server words it: the Today brief's own alert, its words only. */
export interface HeaderAlert {
  /** Tonight's, or tomorrow night's: which of Today's groups the bar opens at. */
  when: 'tonight' | 'tomorrow';
  /** The bar's words, two lines at most: "Frost tonight, 33°F: cover the basil before dark." */
  bar: string;
  /** The alert's title, "Frost tonight, 33°F" (the bar falls back to it). */
  title: string;
}

/** The weather now (B77: the forecast period covering this moment), as the server says it. */
export interface HeaderNow {
  /** The reading in the reader's unit, "64°F". */
  display: string;
  /** The sky in the reader's language, "Mostly sunny" or "Mayormente soleado". */
  words: string;
  /** The sky's icon key. */
  icon: SkyKey;
  /** Whether it is the day's period. */
  isDaytime: boolean;
  /** The period's name in the reader's language, "This Afternoon" or "Esta tarde"; null for none. */
  label: string | null;
}

/** The header answer. */
export interface HeaderAnswer {
  /** Always true on a 200. */
  success: boolean;
  /** The garden's day, "YYYY-MM-DD". */
  date: string;
  /** The language the words are in. */
  lang: string;
  /** The unit the reading is in. */
  unit: 'F' | 'C';
  /** The frost bar's alert, or null for none (or when it could not be built: the server reports that). */
  alert: HeaderAlert | null;
  /** The weather now, or null (no forecast, no reading, or it could not be built). */
  now: HeaderNow | null;
  /** The icon the header's pill draws: the snowflake under a frost tonight, else the sky's; null with no weather now. */
  pillIcon: SkyKey | null;
  /** The server's sentence for a garden the weather service cannot place yet (B44), in the reader's language; else null. */
  noForecastMessage: string | null;
  /** How long the phone keeps this answer, in minutes (the Grow setting forecast.KEEP_MINUTES, at least 1). */
  keepMinutes: number;
}

/**
 * Reads the header answer for the garden being tended.
 *
 * @param lang - The reader's language.
 * @param unit - The reader's temperature unit.
 * @returns The answer.
 * @throws {Error} If the request fails or the server answers success false (its own error text).
 */
export async function getHeader(lang: string, unit: 'F' | 'C'): Promise<HeaderAnswer> {
  const response = await apiClient.get<HeaderAnswer & { error?: string }>('/header', { params: { lang, unit } });
  if (!response.data.success) throw new Error(response.data.error || 'Failed to read the header answer');
  return response.data;
}
